import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { IconClose } from 'components/Icons';
import './SlidePanel.scss';

export interface SlidePanelProps {
  /**
   * Whether the panel is open
   */
  isOpen: boolean;
  /**
   * Callback when the panel is closed
   */
  onClose: () => void;
  /**
   * Panel title (optional)
   */
  title?: string;
  /**
   * Icon to display next to the title (optional)
   */
  titleIcon?: React.ReactNode;
  /**
   * Whether to show an explicit close button in the header
   */
  showExplicitCloseButton?: boolean;
  /**
   * Panel content
   */
  children: React.ReactNode;
  /**
   * Whether to show a BETA badge next to the title
   */
  showBetaBadge?: boolean;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Panel position (default: 'right')
   */
  position?: 'right' | 'left' | 'top' | 'bottom';
  /**
   * Width of the panel (for right/left positions)
   */
  width?: string;
  /**
   * Height of the panel (for top/bottom positions)
   */
  height?: string;
}

/**
 * A slide panel component that slides in from any edge of the screen
 */
const SlidePanel: React.FC<SlidePanelProps> = ({
  isOpen,
  onClose,
  title,
  titleIcon,
  showExplicitCloseButton = false,
  showBetaBadge = false,
  children,
  className,
  position = 'right',
  width = '400px',
  height = '400px',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle animation timing
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Use a short timeout to ensure the DOM has updated before adding the open class
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300); // Match transition duration in CSS
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node) && isOpen) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isVisible && !isOpen) {
    return null;
  }

  const style: React.CSSProperties = {};
  if (position === 'right' || position === 'left') {
    style.width = width;
  } else {
    style.height = height;
  }

  return (
    <div className={classNames('slide-panel-container', { open: isOpen, animating: isAnimating })}>
      <div className="slide-panel-overlay" onClick={onClose} />
      <div
        ref={panelRef}
        className={classNames('slide-panel', `slide-panel-${position}`, { open: isAnimating }, className)}
        style={style}
      >
        <div
          className={classNames('slide-panel-header', {
            'slide-panel-header--center': !showExplicitCloseButton,
          })}
        >
          <div className="slide-panel-title-container">
            {titleIcon && <div className="slide-panel-icon">{titleIcon}</div>}
            <div className="slide-panel-title-wrapper">
              {title && (
                <h3 id="slide-panel-title" className="slide-panel-title">
                  {title}
                </h3>
              )}
              {showBetaBadge && <span className="slide-panel-beta-badge">BETA</span>}
            </div>
          </div>
          {showExplicitCloseButton ? (
            <button type="button" className="slide-panel-close-button" onClick={onClose} aria-label="Close panel">
              <IconClose />
            </button>
          ) : (
            <button className="slide-panel-close" onClick={onClose} aria-label="Close panel">
              <IconClose />
            </button>
          )}
        </div>
        <div className="slide-panel-content">{children}</div>
      </div>
    </div>
  );
};

export default SlidePanel;
